import classNames from "classnames";
import { FC, ReactNode, useContext, useEffect, useRef } from "react";

import { TransportIdentifier } from "../../redux/types";

import { generateTextColourFromBackgroundColour } from "../../utils/textColour";

import { MapContext } from "../Map/Map";
import { UIContext } from "contexts/UIContext";

import Label from "../Label/Label";
import NoDepartures from "../NoDepartures/NoDepartures";

import styles from "./TransportTimetable.module.scss";
import { useTranslation } from "react-i18next";

export type TransportTimetableDeparture = {
  transportIdentifier: TransportIdentifier;
  destination: string;
  departureTime: string;
  departureTimeISO: string;
};

interface Props {
  dataQA: string;
  className?: string;
  departures: TransportTimetableDeparture[];
}

const useGenerateDepartureListItems = (
  departures: TransportTimetableDeparture[],
): ReactNode[] => {
  const labelRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const maxWidth = Math.max(
      ...labelRefs.current.map((ref) => ref?.offsetWidth ?? 0),
    );
    labelRefs.current.forEach((ref) => {
      if (ref) ref.style.minWidth = `${maxWidth}px`;
    });
  }, [departures]);

  return departures.map((departure, index) => (
    <li className={styles.listItem} key={index}>
      <div
        ref={(el) => (labelRefs.current[index] = el)}
        style={{ display: "inline-block" }}
      >
        <Label
          dataQA={`departure-chip-${departure.transportIdentifier.code}`}
          className={styles.label}
          text={departure.transportIdentifier.code}
          {...(departure.transportIdentifier.colour && {
            backgroundColor: departure.transportIdentifier.colour,
            color: generateTextColourFromBackgroundColour(
              departure.transportIdentifier.colour,
            ),
          })}
          active
        />
      </div>
      <p className={styles.destination}>{departure.destination}</p>
      <p className={styles.departureTime}>{departure.departureTime}</p>
    </li>
  ));
};

const TransportTimetable: FC<Props> = ({ departures, className, dataQA }) => {
  const { controlTheme } = useContext(MapContext);
  const { screenOrientation } = useContext(UIContext);
  const { t } = useTranslation();

  const departureListItems = useGenerateDepartureListItems(departures);

  return departureListItems.length ? (
    <ul
      data-qa={dataQA}
      className={classNames(styles.container, className, styles[controlTheme], {
        [styles.landscape]: screenOrientation === "landscape",
      })}
    >
      {departureListItems}
    </ul>
  ) : (
    <NoDepartures title={t("transport_timetable.no_departures")} />
  );
};

export default TransportTimetable;
